import { Injectable } from '@angular/core';
import { Firestore, doc, getDoc, docData, query, collectionData, collection, getDocs, where, setDoc, updateDoc } from '@angular/fire/firestore';
import { from, map, Observable } from 'rxjs';
import { UserService } from './user.service';
import {UserProfile, UserUnits} from '../models/user';

export interface ReproductiveHealth {
  cycleLengthPredicted: number;
  periodLengthPredicted: number;
  shouldTrackFertileWindow: boolean | null;
  contraception: string[] | null,
  usageReason: string | null,
}

@Injectable({ providedIn: 'root' })
export class UserInfoService {
  constructor(private fs: Firestore, private userService: UserService) { }

  public hasDevices$ = collectionData(
    query(
      collection(this.fs, `v0/data/user/${this.userService.user.id}/bellabeatDevices`),
    ),
  ).pipe(
    map(docs => docs.length > 0)
  );

  public isDataEncrypted$ = docData(
    doc(this.fs, `v0/data/user/${this.userService.user.id}`)
  ).pipe(
    map((doc: any) => {
      return doc?.periodDataEncrypted === true;
    })
  );
  async hasIVPDeviceOnce(): Promise<boolean> {
    const devicesColl = collection(this.fs, `v0/data/user/${this.userService.user.id}/bellabeatDevices`);
    const q = query(devicesColl, where('type', '==', 'IVP'));
    const snap = await getDocs(q);

    return !snap.empty;
  }

  hasIVPDevice(): Observable<boolean> {
    const devicesColl = collection(this.fs, `v0/data/user/${this.userService.user.id}/bellabeatDevices`);
    const q = query(devicesColl, where('type', '==', 'IVP'));

    return from(
      getDocs(q).then((snap) => !snap.empty)
    );
  }

  async getReproductiveHealth(): Promise<ReproductiveHealth | null> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'reproductiveHealth');
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as ReproductiveHealth) : null;
  }

  async getUserName(): Promise<string | null> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'profile');
    const snap = await getDoc(ref);
    return snap.exists()
      ? ((snap.data() as { name?: string | null }).name ?? null)
      : null;
  }

  async saveUserName(name: string | null): Promise<void> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'profile');
    await updateDoc(ref, { name: name ?? null });
  }

  async saveUsageReason(usageReason: string | null): Promise<void> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'reproductiveHealth');

    await setDoc(
      ref,
      { usageReason: usageReason ?? null },
      { merge: true }
    );
  }

  async saveUserDateOfBirth(dateOfBirth: string | null): Promise<void> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'profile');
    console.log(dateOfBirth);
    await updateDoc(ref, { dateOfBirth: dateOfBirth ?? null });
  }

  async getUserChatPermission(): Promise<boolean> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'permissions');
    const snap = await getDoc(ref);

    return !!(snap.data()?.['healthAssistantConsent']);
  }

  async saveUserChatPermission(consented: boolean): Promise<void> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'permissions');

    await setDoc(
      ref,
      {
        healthAssistantConsent: consented,
      },
      { merge: true }
    );
  }

  getReproductiveHealth$(): Observable<ReproductiveHealth | undefined> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'reproductiveHealth');
    return docData(ref) as Observable<ReproductiveHealth | undefined>;
  }

  getUserProfile$(): Observable<UserProfile | undefined> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'profile');
    return docData(ref) as Observable<UserProfile | undefined>;
  }

  async getUserProfile(): Promise<UserProfile | undefined> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'profile');
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as UserProfile) : undefined;
  }

  async getUserUnits(): Promise<UserUnits | undefined> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'units');
    const snap = await getDoc(ref);
    return snap.exists() ? (snap.data() as UserUnits) : undefined;
  }

  async saveUserUnits(units: Partial<UserUnits>): Promise<void> {
    const uid = this.userService.user.id;
    const ref = doc(this.fs, 'v0', 'data', 'user', uid, 'userInfo', 'units');
    await setDoc(ref, units, { merge: true });
  }

  getUserInfoContraception(userInfoHealth: ReproductiveHealth): string[] {
    if (!userInfoHealth?.contraception) {
      return []
    }
    return this._mapContraceptionToTitles(userInfoHealth.contraception);
  }

  _mapContraceptionToTitles(values?: string[] | null): string[] {
    if (!values || values.length === 0) return [];

    const TITLE_MAP: Record<string, string> = {
      contraceptive_pill: "Contraceptive pills",
      contraceptive_patch: "Contraceptive patch",
      intrauterine_device: "Intrauterine device",
      vaginal_ring: "Vaginal ring",
      other: "Other",
      none: "I don’t use hormonal contraception",
    };

    const seen = new Set<string>();
    const titles: string[] = [];

    for (const v of values) {
      const key = String(v).toLowerCase();
      const title = TITLE_MAP[key];
      if (title && !seen.has(title)) {
        titles.push(title);
        seen.add(title);
      }
    }
    return titles;
  }
}
